import dayjs from "../../../../../../utils/dayjsConfig";
import { ReactElement, memo, useEffect, useState } from "react";
import { Currency } from "../../../../types";
import { AccordionTableColumn, CedearsColumn, CedearsRow } from "../../types";

//ASSETS
import EmptyStar from "../../../../../../assets/images/icons/empty-star-icon.svg?react";
import YellowStar from "../../../../../../assets/images/icons/filled-star-icon.svg?react";
import { asMoney } from "../../../../util";
import { InstrumentActions, InstrumentDetail } from "../Stocks";
import { createPriceClickHandler } from "../../utils";
import styles from "./Cedears.module.scss";
import { Dispatch, UnknownAction } from "@reduxjs/toolkit";

const {
  instrumentCell,
  instrumentIcon,
  instrumentName,
  lastPriceCell,
  lastPriceText,
  lastPriceCurrency,
  variationCellPositive,
  variationCellNegative,
  shareContainer,
  buySharePrice,
  sellSharePrice,
  volumeCell,
  actionsData,
  buyData,
  sellData,
  underlined,
} = styles;

const labelsForCurrency: { [key in Currency]: string } = {
  ARS: "ARS",
  DOLLAR_CCL: "CCL",
  DOLLAR_MEP: "MEP",
};

export const cedearsColumns: AccordionTableColumn<CedearsColumn>[] = [
  { key: "instrument", label: "Instrumento", align: "start", sortable: true },
  { key: "lastPrice", label: "Último precio", align: "end", sortable: true },
  { key: "variation", label: "Variación", align: "end", sortable: true },
  { key: "sell", label: "Vender", align: "end" },
  { key: "buy", label: "Comprar", align: "end" },
  { key: "volume", label: "Volumen", align: "end", sortable: true },
  { key: "volumeAmount", label: "Volumen monto", align: "end", sortable: true },
  { key: "vwap", label: "VWAP", align: "end", sortable: true },
  { key: "starred", label: "", align: "center" },
];

export const Instrument = memo(
  ({
    icon,
    symbol,
    description,
  }: {
    symbol: string;
    icon?: ReactElement;
    description?: string;
  }) => (
    <div className={instrumentCell}>
      {!!icon && <div className={instrumentIcon}>{icon}</div>}
      <div className={instrumentName}>
        <h1>{symbol}</h1>
        <span>{description}</span>
      </div>
    </div>
  ),
);
Instrument.displayName = "Instrument";

export const LastPrice = memo(
  ({
    value,
    currency,
    updatedAt,
  }: {
    value?: number;
    currency: Currency;
    updatedAt?: string;
  }) => (
    <div className={lastPriceCell}>
      <div className={lastPriceText}>
        <h2>
          {currency.includes("DOLLAR") ? "U$S " : "$"}
          {asMoney(value ?? 0)}
        </h2>
        {!!updatedAt && <span>{dayjs(updatedAt).format("HH:mm:ss")}</span>}
      </div>

      <div className={lastPriceCurrency}>{labelsForCurrency[currency]}</div>
    </div>
  ),
);

export const Variation = memo(
  ({ percent, value }: { percent: number; value: number }) => (
    <div
      className={percent >= 0 ? variationCellPositive : variationCellNegative}
    >
      <b>{asMoney(percent)}%</b>
      <span>{asMoney(value)}</span>
    </div>
  ),
);

export const SharePrice = memo(
  ({
    data,
    buy,
    onClick,
    isEnabledOperation,
    canSell,
  }: {
    data: { size: number; price: number }[];
    buy?: boolean;
    onClick: (price: number) => void;
    isEnabledOperation: boolean;
    canSell?: boolean;
  }) => {
    const [{ size, price }] = data;
    const isInteractable =
      (buy && isEnabledOperation) || (!buy && canSell && isEnabledOperation);

    return (
      <div className={shareContainer}>
        <div
          className={`${buy ? buySharePrice : sellSharePrice} ${
            isInteractable ? "cursor-pointer" : "cursor-default"
          }`}
          {...(isInteractable && {
            onClick: () => onClick(price),
          })}
        >
          <p>{size?.toLocaleString("es-AR")} x</p>
          <b>{asMoney(price)}</b>
        </div>
      </div>
    );
  },
);

export const Volume = memo(({ value }: { value?: number }) => (
  <span className={volumeCell}>{(value ?? 0).toLocaleString("es-AR")}</span>
));

export const Duration = memo(({ value }: { value?: number }) => (
  <span className={volumeCell}>
    {value !== undefined
      ? value.toLocaleString("es-AR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "-"}
  </span>
));

export const Starred = memo(
  ({
    isStarred,
    onStarClicked,
  }: {
    isStarred: boolean;
    onStarClicked?: () => void;
  }) => {
    // Local state to override prop value after click
    const [localStarred, setLocalStarred] = useState<boolean | null>(null);
    const [expectedValue, setExpectedValue] = useState<boolean | null>(null);

    // Sync back to prop ONLY when it matches the expected value
    useEffect(() => {
      if (expectedValue !== null && isStarred === expectedValue) {
        // Prop now matches what we clicked, clear local override
        setLocalStarred(null);
        setExpectedValue(null);
      }
    }, [isStarred, expectedValue]);

    // Use local override if exists, otherwise use prop value
    const displayStarred = localStarred !== null ? localStarred : isStarred;

    const handleClick = (e: React.MouseEvent) => {
      // Prevent event propagation to avoid conflicts with table row handlers
      e.stopPropagation();

      const newValue = !displayStarred;

      // Update local state IMMEDIATELY for instant UI feedback
      setLocalStarred(newValue);
      setExpectedValue(newValue);

      // Execute parent callback (this should update the state and call the API)
      onStarClicked?.();
    };

    return (
      <div
        className="cursor-pointer select-none w-6 h-6 flex items-center justify-center"
        onClick={handleClick}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {displayStarred ? <YellowStar /> : <EmptyStar />}
      </div>
    );
  },
  // Only re-render if isStarred prop changes
  (prevProps, nextProps) => {
    return prevProps.isStarred === nextProps.isStarred;
  },
);

export const Tir = memo(({ value }: { value?: number }) => (
  <div className={volumeCell}>{value ? `${asMoney(value)}%` : "-"}</div>
));

export const SharePriceDetail = memo(
  ({
    buy,
    data,
    onClick,
    isEnabledOperation,
    canSell,
  }: {
    data: { size: number; price: number }[];
    buy?: boolean;
    onClick: (price: number) => void;
    isEnabledOperation: boolean;
    canSell?: boolean;
  }) => {
    if (!data || data.length <= 1) {
      return <></>;
    }

    const isInteractable =
      (buy && isEnabledOperation) || (!buy && canSell && isEnabledOperation);
    return (
      <div className={actionsData}>
        {data.slice(1).map(({ size, price }, i) => (
          <div
            key={i}
            className={`${buy ? buyData : sellData} ${
              isInteractable ? `cursor-pointer ${underlined}` : "cursor-default"
            }`}
            {...(isInteractable && {
              onClick: () => onClick(price),
            })}
          >
            <p>
              {size?.toLocaleString("es-AR")} x <b>{asMoney(price)}</b>
            </p>
          </div>
        ))}
      </div>
    );
  },
);

export const VolumeAmount = memo(
  ({
    value,
    currency,
    showPercentage = false,
  }: {
    value?: number;
    currency: Currency;
    showPercentage?: boolean;
  }) => (
    <>
      {showPercentage ? (
        <span className={volumeCell}>
          {((value ?? 0) * 100).toLocaleString("es-AR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          })}
          %
        </span>
      ) : (
        <span className={volumeCell}>
          {currency.includes("DOLLAR") ? "U$S " : "$"}
          {(value ?? 0).toLocaleString("es-AR")}
        </span>
      )}
    </>
  ),
  (prevProps, nextProps) => prevProps.value === nextProps.value,
);

export const getAccordionCedearsCellValue = ({
  column,
  row: {
    quote: {
      key,
      symbol,
      lastTrade,
      currency,
      lastTradeUpdatedAt,
      percentChange,
      volumeAmount,
      bids,
      offers,
      nominalVariation,
      volume,
      weightedAveragePrice,
      type,
    },
    staticQuote: { company, icon },
    isStarred,
    onStarClicked,
  },
  dispatch,
  accountNumber,
  isEnabledOperation,
  canSellMap = {},
  settlement,
}: {
  column: CedearsColumn;
  row: CedearsRow;
  accountNumber: string | undefined;
  dispatch: Dispatch<UnknownAction>;
  isEnabledOperation: boolean;
  canSellMap?: Record<string, boolean>;
  settlement?: string;
}) => {
  const baseSymbolName = currency === "ARS" ? symbol : `${symbol.slice(0, -1)}`;

  switch (column) {
    case "instrument":
      return (
        <Instrument
          symbol={symbol}
          icon={icon}
          description={company}
          key={key}
        />
      );
    case "lastPrice":
      return (
        <LastPrice
          value={lastTrade}
          currency={currency}
          updatedAt={lastTradeUpdatedAt}
        />
      );
    case "variation":
      return <Variation percent={percentChange} value={nominalVariation} />;
    case "sell":
      return (
        <SharePrice
          data={bids}
          onClick={createPriceClickHandler(
            accountNumber,
            symbol,
            type,
            "SELL",
            dispatch,
            settlement,
          )}
          isEnabledOperation={isEnabledOperation}
          canSell={canSellMap[baseSymbolName]}
        />
      );
    case "buy":
      return (
        <SharePrice
          buy
          data={offers}
          onClick={createPriceClickHandler(
            accountNumber,
            symbol,
            type,
            "BUY",
            dispatch,
            settlement,
          )}
          isEnabledOperation={isEnabledOperation}
        />
      );
    case "volume":
      return <Volume value={volume} />;
    case "volumeAmount":
      return <VolumeAmount value={volumeAmount} currency={currency} />;
    case "vwap":
      return <VolumeAmount value={weightedAveragePrice} currency={currency} />;
    case "starred":
      return <Starred isStarred={isStarred} onStarClicked={onStarClicked} />;
    default:
      return "";
  }
};

export const getCedearsCellValue = ({
  column,
  row: {
    quote,
    staticQuote: { icon },
  },
  accountNumber,
  dispatch,
  isEnabledOperation,
  canSellMap = {},
  settlement,
}: {
  column: CedearsColumn;
  row: CedearsRow;
  accountNumber: string | undefined;
  dispatch: Dispatch<UnknownAction>;
  isEnabledOperation: boolean;
  canSellMap?: Record<string, boolean>;
  settlement?: string;
}) => {
  const {
    symbol,
    openingPrice,
    closingPrice,
    highestDailyTrade,
    lowestDailyTrade,
    bids,
    offers,
    type,
    currency,
  } = quote;

  const baseSymbolName = currency === "ARS" ? symbol : `${symbol.slice(0, -1)}`;

  switch (column) {
    case "instrument":
      return (
        <InstrumentDetail
          openingPrice={openingPrice}
          closingPrice={closingPrice}
          highestDailyTrade={highestDailyTrade}
          lowestDailyTrade={lowestDailyTrade}
          icon={icon}
        />
      );
    case "sell":
      return (
        <SharePriceDetail
          data={bids}
          onClick={createPriceClickHandler(
            accountNumber,
            symbol,
            type,
            "SELL",
            dispatch,
            settlement,
          )}
          isEnabledOperation={isEnabledOperation}
          canSell={canSellMap[baseSymbolName]}
        />
      );
    case "buy":
      return (
        <SharePriceDetail
          buy
          data={offers}
          onClick={createPriceClickHandler(
            accountNumber,
            symbol,
            type,
            "BUY",
            dispatch,
            settlement,
          )}
          isEnabledOperation={isEnabledOperation}
        />
      );
    case "starred":
      return (
        <InstrumentActions
          symbol={quote.symbol}
          type={quote.type}
          settledPeriod={quote.settledPeriod}
          currency={quote.currency}
        />
      );

    default:
      return <></>;
  }
};
